import {
	Button,
	Dialog,
	FormattedText,
	Spinner,
	Toolbar,
	Typography
} from "client/Components";
import { useDataInputDialog } from "client/Hooks";
import { useUnmountedCancellation } from "client/Hooks/useUnmountedCancellation";
import { AlertManager, useAlerts } from "client/Hooks/useAlerts";
import { localize } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	useCallback,
	useMemo,
	useState
} from "react";
import {
	AppAvailabilityModel,
	SharedSpaceTemplateAppsAvailabilityModel,
	SpaceTemplateAvailabilityKind
} from "client/Api/Workspace/Templates/sharedSpaceTemplate";
import { disableApp, enableApp } from "client/Api/Apps/AppStore/appStore";
import { isNotNil, setTrackingId } from "client/Misc/utils";
import { never } from "client_legacy/ndaw/errors";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { AppIconAndName } from "./AppIconAndName";
import styles from "./SpaceTemplateAppsAvailabilityDialog.module.less";
import { sortBy } from "client_legacy/ndaw/arrayHelpers";

export type SpaceTemplateAppsAvailabilityDialogProps = {
	onClose: () => void;
	onSave: (enabledApps?: readonly AppItemModel[]) => void;
	appsAvailability: UnavailableOrDisabledAppsAvailabilityModel;
};

export const SpaceTemplateAppsAvailabilityDialog = (props: SpaceTemplateAppsAvailabilityDialogProps): ReactElement => {
	const {
		onClose,
		onSave,
		appsAvailability
	} = props;

	const sortedAppsAvailability = useMemo(
		(): UnavailableOrDisabledAppsAvailabilityModel => {
			return sortAppsAvailabilityByName(appsAvailability);
		},
		[appsAvailability]
	);

	switch (sortedAppsAvailability.kind) {
		case SpaceTemplateAvailabilityKind.SomeAppsDisabled:
		case SpaceTemplateAvailabilityKind.AllAppsDisabled:
			return (
				<AppsDisabledDialog
					onClose={onClose}
					onSave={onSave}
					apps={sortedAppsAvailability.disabledApps}
					kind={sortedAppsAvailability.kind}
				/>
			);
		case SpaceTemplateAvailabilityKind.SomeAppsUnavailable:
			return (
				<SomeAppsUnavailableDialog
					onClose={onClose}
					onSave={onSave}
					apps={sortedAppsAvailability.unavailableApps}
				/>
			);
		case SpaceTemplateAvailabilityKind.AllAppsUnavailable:
			return (
				<AllAppsUnavailableDialog
					onClose={onClose}
					apps={sortedAppsAvailability.unavailableApps}
				/>
			);
		default:
			never(sortedAppsAvailability);
	}
};
SpaceTemplateAppsAvailabilityDialog.displayName = "SpaceTemplateAppsAvailabilityDialog";

export type SpaceTemplateAppsAvailabilityDialogInitialData = Omit<
	SpaceTemplateAppsAvailabilityDialogProps,
	"onSave" | "onClose"
>;

export function useSpaceTemplateAppsAvailabilityDialog(): [
	element: ReactElement | null,
	open: (props: SpaceTemplateAppsAvailabilityDialogInitialData) => Promise<TryResult<readonly AppItemModel[] | undefined>>
] {
	return useDataInputDialog(
		(props, onClose, onSave) => (
			<SpaceTemplateAppsAvailabilityDialog
				{...props}
				onSave={(enabledApps): void => onSave(enabledApps)}
				onClose={onClose}
			/>
		)
	);
}

type UnavailableOrDisabledAppsAvailabilityModel = Exclude<
	SharedSpaceTemplateAppsAvailabilityModel,
	{ kind: SpaceTemplateAvailabilityKind.AllAppsEnabled; }
>;

function sortAppsAvailabilityByName(
	appsAvailability: UnavailableOrDisabledAppsAvailabilityModel
): UnavailableOrDisabledAppsAvailabilityModel {
	switch (appsAvailability.kind) {
		case SpaceTemplateAvailabilityKind.SomeAppsDisabled:
		case SpaceTemplateAvailabilityKind.AllAppsDisabled:
			return {
				kind: appsAvailability.kind,
				disabledApps: sortBy(
					appsAvailability.disabledApps,
					x => x.name
				)
			};
		case SpaceTemplateAvailabilityKind.SomeAppsUnavailable:
		case SpaceTemplateAvailabilityKind.AllAppsUnavailable:
			return {
				kind: appsAvailability.kind,
				unavailableApps: sortBy(
					appsAvailability.unavailableApps,
					x => x.name
				)
			};
		default:
			never(appsAvailability);
	}
};

type AppsDisabledDialogProps = {
	onClose: () => void;
	onSave: (enabledApps: readonly AppItemModel[]) => void;
	apps: readonly AppAvailabilityModel[];
	kind: SpaceTemplateAvailabilityKind.AllAppsDisabled | SpaceTemplateAvailabilityKind.SomeAppsDisabled;
};

const AppsDisabledDialog = (props: AppsDisabledDialogProps): ReactElement => {
	const {
		onClose,
		onSave,
		apps,
		kind
	} = props;

	const [appList, setAppList] = useState<readonly AppItemModel[]>(
		(): readonly AppItemModel[] => apps.map(
			(app): AppItemModel => ({ ...app, installedAppId: null })
		)
	);

	const alertManager: AlertManager = useAlerts();

	const changeAppStateHandler = useCallback(
		(mainAppId: number, installedAppId: number | null): void => {
			setAppList(
				(prev): readonly AppItemModel[] => prev.map(
					(item): AppItemModel => {
						if (item.mainAppId === mainAppId) {
							return {
								...item,
								installedAppId
							};
						}

						return item;
					}
				)
			);
		},
		[]
	);

	const enabledApps: readonly AppItemModel[] = useMemo(
		(): readonly AppItemModel[] => appList.filter(
			(app): boolean => isNotNil(app.installedAppId)
		),
		[appList]
	);

	const saveHandler = useCallback(
		(): void => {
			onSave(enabledApps);
		},
		[enabledApps, onSave]
	);

	const isUseTemplateEnabled: boolean = useMemo(
		(): boolean => {
			if (kind === SpaceTemplateAvailabilityKind.SomeAppsDisabled) {
				return true;
			}

			return enabledApps.length > 0;
		},
		[enabledApps, kind]
	);

	return (
		<Dialog
			trackingId="apps-disabled-dialog"
		>
			<Dialog.Header
				title={localize("_SomeAppsAreDisabled")}
				onClose={onClose}
			/>
			<Dialog.Body>
				<p className={styles.description}>
					<FormattedText
						text={localize("_SomeAppsAreDisabledDescription")}
						thingsToWrap={[
							{
								blockId: "Note",
								wrap: (children) => (
									<Typography weight="bold">
										{children}
									</Typography>
								)
							}
						]}
					/>
				</p>
				<p className={styles.title}>
					{localize("_Apps")}
				</p>
				<ul
					{...setTrackingId("app-list")}
					className={styles.appList}
				>
					{appList.map((app) => (
						<li
							{...setTrackingId("app-item", `|${app.mainAppId}`)}
							key={app.mainAppId}
						>
							<ToggleAppStatusCard
								app={app}
								alertManager={alertManager}
								onChange={(installedAppId: number | null): void => {
									changeAppStateHandler(app.mainAppId, installedAppId);
								}}
							/>
						</li>
					))}
				</ul>
			</Dialog.Body>
			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						text={localize("_UseTemplate")}
						onClick={saveHandler}
						trackingId="use-template"
						isDisabled={!isUseTemplateEnabled}
					/>
					<Toolbar.Button
						kind="secondary"
						text={localize("_Cancel")}
						onClick={onClose}
						trackingId="cancel"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
AppsDisabledDialog.displayName = "AppsDisabledDialog";

export type AppItemModel = AppAvailabilityModel & {
	installedAppId: number | null;
};

type ToggleAppStatusCardProps = {
	app: AppItemModel;
	onChange: (installedAppId: number | null) => void;
	alertManager: AlertManager;
};

const ToggleAppStatusCard = (props: ToggleAppStatusCardProps): ReactElement => {
	const {
		app,
		alertManager,
		onChange,
	} = props;

	const [isBusy, setIsBusy] = useState(false);
	const isEnabled: boolean = isNotNil(app.installedAppId);

	const globalCancellation: AbortSignal = useUnmountedCancellation();

	const changeAppStateHandler = useCallback(
		async (): Promise<void> => {
			if (isBusy) {
				return;
			}

			setIsBusy(true);
			let installedAppId: number | null = null;

			try {
				if (app.installedAppId) {
					await disableApp(app.installedAppId, globalCancellation);
				} else {
					installedAppId = await enableApp(app.mainAppId, globalCancellation);
				}

				setIsBusy(false);
				onChange(installedAppId);
			} catch (error) {
				if (isCancelledError(error)) {
					return;
				}
				setIsBusy(false);
				alertManager.showError(localize("_GeneralErrorMessage"));
			}
		},
		[
			alertManager,
			app.installedAppId,
			app.mainAppId,
			globalCancellation,
			isBusy,
			onChange
		]
	);

	return (
		<Dialog.Card>
			<div className={styles.appItem}>
				<div className={styles.appIconAndName}>
					<AppIconAndName
						icon={app.imageDisplayInfo}
						color={app.color}
						name={app.name}
					/>
				</div>
				<div className={styles.appItemButtonAndSpinner}>
					{isBusy && (
						<Spinner />
					)}
					{isEnabled
						? (
							<Button
								kind="secondary"
								text={localize("_Disable")}
								onClick={changeAppStateHandler}
								trackingId="disable"
							/>
						)
						: (
							<Button
								kind="primary"
								text={localize("_Enable")}
								onClick={changeAppStateHandler}
								trackingId="enable"
							/>
						)
					}
				</div>
			</div>
		</Dialog.Card>
	);
};
ToggleAppStatusCard.displayName = "ToggleAppStatusCard";

type SomeAppsUnavailableDialogProps = {
	onClose: () => void;
	onSave: () => void;
	apps: readonly AppAvailabilityModel[];
};

const SomeAppsUnavailableDialog = (props: SomeAppsUnavailableDialogProps): ReactElement => {
	const {
		onClose,
		onSave,
		apps
	} = props;

	return (
		<Dialog
			trackingId="some-apps-unavailable-dialog"
		>
			<Dialog.Header
				title={localize("_SomeAppsAreNotAvailable")}
				onClose={onClose}
			/>
			<Dialog.Body>
				<p className={styles.description}>
					<FormattedText
						text={localize("_SomeAppsAreNotAvailableDescription")}
						thingsToWrap={[
							{
								blockId: "Note",
								wrap: (children) => (
									<Typography weight="bold">
										{children}
									</Typography>
								)
							}
						]}
					/>
				</p>
				<p className={styles.title}>
					{localize("_NotAvailableDisabled")}
				</p>
				<ul
					{...setTrackingId("app-list")}
				>
					{apps.map((app) => (
						<li
							{...setTrackingId("app-item", `|${app.mainAppId}`)}
							key={app.mainAppId}
							className={styles.appListItem}
						>
							<AppIconAndName
								icon={app.imageDisplayInfo}
								color={app.color}
								name={app.name}
								isDisabled
							/>
						</li>
					))}
				</ul>
			</Dialog.Body>
			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						text={localize("_UseTemplate")}
						onClick={onSave}
						trackingId="use-template"
					/>
					<Toolbar.Button
						kind="secondary"
						text={localize("_Cancel")}
						onClick={onClose}
						trackingId="cancel"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
SomeAppsUnavailableDialog.displayName = "SomeAppsUnavailableDialog";

type AllAppsUnavailableDialogProps = {
	onClose: () => void;
	apps: readonly AppAvailabilityModel[];
};

const AllAppsUnavailableDialog = (props: AllAppsUnavailableDialogProps): ReactElement => {
	const {
		onClose,
		apps
	} = props;

	return (
		<Dialog
			trackingId="all-apps-unavailable-dialog"
		>
			<Dialog.Header
				title={localize("_CantUseThisTemplate")}
				onClose={onClose}
			/>
			<Dialog.Body>
				<p className={styles.description}>
					<FormattedText
						text={localize("_CantUseThisTemplateDescription")}
						thingsToWrap={[
							{
								blockId: "Note",
								wrap: (children) => (
									<Typography weight="bold">
										{children}
									</Typography>
								)
							}
						]}
					/>
				</p>
				<p className={styles.title}>
					{localize("_NotAvailableDisabled")}
				</p>
				<ul
					{...setTrackingId("app-list")}
				>
					{apps.map((app) => (
						<li
							{...setTrackingId("app-item", `|${app.mainAppId}`)}
							key={app.mainAppId}
							className={styles.appListItem}
						>
							<AppIconAndName
								icon={app.imageDisplayInfo}
								color={app.color}
								name={app.name}
								isDisabled
							/>
						</li>
					))}
				</ul>
			</Dialog.Body>
			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="secondary"
						text={localize("_Close")}
						onClick={onClose}
						trackingId="close"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
AllAppsUnavailableDialog.displayName = "AllAppsUnavailableDialog";
